import axios from 'axios';
import { Map, Marker, Popup, NavigationControl, setWorkerUrl } from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import 'maplibre-gl/dist/maplibre-gl.css';

// MapLibre 6 + Vite: worker must be registered or vector tiles never load
setWorkerUrl(workerUrl);

const DEVICE = window.matchMedia('(max-width: 999px)').matches ? 'mobile' : 'desktop';
const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
const DEFAULT_CENTER = [17.3989466, 51.2120901]; // [lng, lat]

class Kopernika_Map {
  constructor() {
    this.location = "Oleśnica Kopernika 16";
    this.map = null;
    this.mini_map = null;
    this.categories = [
      {
        name: 'Sklepy',
        slug: 'grocery_or_supermarket,supermarket,grocery',
        icon: `${rest.theme_link}/img/map/shop.svg`,
      },
      {
        name: "Kościoły",
        slug: "church",
        icon: `${rest.theme_link}/img/map/church.svg`,
      },
      {
        name: "Restauracje",
        slug: "restaurant",
        icon: `${rest.theme_link}/img/map/restaurant.svg`,
      },
      {
        name: "Placówki pocztowe",
        slug: "post_office",
        icon: `${rest.theme_link}/img/map/post.svg`,
      },
      {
        name: "Szkoły i przedszkola",
        slug: 'school',
        icon: `${rest.theme_link}/img/map/school.svg`,
      },
      {
        name: "Parki",
        slug: 'park',
        icon: `${rest.theme_link}/img/map/park.svg`,
      },
      {
        name: "Sport i rekreacja",
        slug: 'gym',
        icon: `${rest.theme_link}/img/map/sport.svg`,
      },
      {
        name: "Przystanki autobusowe",
        slug: 'przystanek autobusowy',
        type: 'keyword',
        icon: `${rest.theme_link}/img/map/bus.svg`,
      },
      {
        name: "Apteki",
        slug: "pharmacy",
        icon: `${rest.theme_link}/img/map/pharmacy.svg`,
      }
    ];
    this.show_category = {};
    this.show_category[this.categories[5].slug] = this.categories[5].icon;
    this.markers = [];

    this.getLocation();
  }

  createMarkerElement(iconUrl, width = 32, height = width) {
    const el = document.createElement('div');
    el.className = 'map-marker';
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    el.style.cursor = 'pointer';

    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = '';
    img.width = width;
    img.height = height;
    img.draggable = false;
    img.style.display = 'block';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'contain';
    el.appendChild(img);

    return el;
  }

  renderNavbar() {
    let area = document.querySelector('#areaMapNavi');

    for(let [id, category] of this.categories.entries()) {
      let li = document.createElement('li');
      let text = document.createTextNode(category.name);
      if(id == 5) li.classList.add('active');
      li.appendChild(text);
      area.appendChild(li);

      li.addEventListener('click', () => {
        if(this.show_category[category.slug]) {
          li.classList.remove('active');
          delete this.show_category[category.slug];
        } else {
          this.show_category[category.slug] = category.icon;
          li.classList.add('active');
        }
        this.getNearestByCategory();
      });
    }
  }

  renderMap() {
    const center = this.location?.lng != null
      ? [this.location.lng, this.location.lat]
      : DEFAULT_CENTER;

    this.map = new Map({
      container: 'map',
      style: OPENFREEMAP_STYLE,
      center,
      zoom: DEVICE === 'desktop' ? 14 : 13,
      scrollZoom: false,
      doubleClickZoom: false,
      attributionControl: true,
    });

    this.map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');

    this.map.on('load', () => {
      this.map.resize();
      // logo_map.svg is 161x66 — keep aspect ratio (was squashed into 45x45)
      const el = this.createMarkerElement(`${rest.theme_link}/img/logo_map.svg`, 120, 49);
      new Marker({ element: el, anchor: 'bottom' })
        .setLngLat(center)
        .addTo(this.map);

      this.getNearestByCategory();
    });

    this.map.on('error', (e) => {
      console.error('MapLibre map error', e?.error || e);
      document.querySelector('.map-loader')?.classList.remove('loading');
    });
  }

  renderMiniMap() {
    const miniMapEl = document.getElementById('miniMap');
    if (!miniMapEl) return;

    const center = this.location?.lng != null
      ? [this.location.lng, this.location.lat]
      : DEFAULT_CENTER;

    this.mini_map = new Map({
      container: 'miniMap',
      style: OPENFREEMAP_STYLE,
      center,
      zoom: 15,
      scrollZoom: false,
      doubleClickZoom: false,
      attributionControl: true,
    });

    this.mini_map.on('load', () => {
      this.mini_map.resize();
      const el = this.createMarkerElement(`${rest.theme_link}/img/logo_map.svg`, 120, 49);
      const popup = new Popup({ offset: 25 }).setHTML(
        '<strong>Kopernika 16</strong><br><br>ul. Kopernika 16, 56-400 Oleśnica'
      );

      new Marker({ element: el, anchor: 'bottom' })
        .setLngLat(center)
        .setPopup(popup)
        .addTo(this.mini_map);
    });
  }

  getLocation() {
    let base_url = `${rest.url}investment/v2/location`;
    axios.post(base_url, {
      data: {
        type: 'get'
      }
    })
    .then(res => {
      if (!res.data.location) {
        // If location is missing in DB, fall back to backend geocoding
        axios.post(`${rest.url}kopernika/v1/geocode`, {
          address: this.location
        })
        .then(res => {
          this.location = res.data.location;
          if (document.querySelector('#map')) {
            this.renderMap();
            this.renderNavbar();
          }
          this.renderMiniMap();
        });
      } else {
        this.location = JSON.parse(res.data.location);
        if (document.querySelector('#map')) {
          this.renderMap();
          this.renderNavbar();
        }
        this.renderMiniMap();
      }
    });
  }

  getNearestByCategory() {
    const loader = document.querySelector('.map-loader');
    if (loader) loader.classList.add('loading');

    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    if (Object.keys(this.show_category).length === 0) {
      if (loader) loader.classList.remove('loading');
      return;
    }

    Object.keys(this.show_category).forEach(type => {
      axios.post(`${rest.url}kopernika/v1/places`, {
        place_type: type
      })
      .then(res => {
        this.renderMarkers(res.data.places, type);
      })
      .catch(() => {
        document.querySelector('.map-loader')?.classList.remove('loading');
      });
    });
  }

  renderMarkers(places, type) {
    if (!this.map || !places) {
      const loader = document.querySelector('.map-loader');
      if (loader) loader.classList.remove('loading');
      return;
    }

    places.forEach(place => {
      const iconUrl = this.show_category[type] || place.icon;
      const el = this.createMarkerElement(iconUrl, 32, 32);

      let content = `<p>${place.name}</p>`;
      if (place.rating) {
        content += `<p>Ocena: ${place.rating}</p>`;
      }

      const popup = new Popup({ offset: 25 }).setHTML(content);
      const marker = new Marker({ element: el, anchor: 'bottom' })
        .setLngLat([place.geometry.location.lng, place.geometry.location.lat])
        .setPopup(popup)
        .addTo(this.map);

      this.markers.push(marker);
    });

    const loader = document.querySelector('.map-loader');
    if (loader) loader.classList.remove('loading');
  }
}

export default Kopernika_Map;
